import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import useAlertStore from '@/stores/alertStore';
import { errorReporter, isNetworkError } from '@/services/errorReportingService';
import { createComment, deleteComment, updateComment } from '../services/commentService';
import { commentsQueryKey } from '../config';
import type { Comment, CreateCommentInput } from '../types';

interface UseCommentActionsArgs {
  noteId: string;
  shareToken?: string | null;
}

/**
 * Mutations for the comment system (create / edit / delete). Each invalidates
 * the note's comment query on success so the list and timeline markers refresh.
 * Errors are reported and surfaced to the user with a friendly message.
 */
export const useCommentActions = ({ noteId, shareToken }: UseCommentActionsArgs) => {
  const queryClient = useQueryClient();
  const showAlert = useAlertStore((s) => s.showAlert);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: commentsQueryKey(noteId) }),
    [queryClient, noteId],
  );

  const create = useCallback(
    async (input: CreateCommentInput): Promise<Comment | null> => {
      setIsSubmitting(true);
      try {
        const comment = await createComment({ noteId, shareToken, input });
        await invalidate();
        return comment;
      } catch (error) {
        if (isNetworkError(error)) {
          errorReporter.expected('Failed to create comment (network)', error, {
            feature: 'notes',
            operation: 'createComment',
            noteId,
          });
        } else {
          errorReporter.critical('Failed to create comment', error, {
            feature: 'notes',
            operation: 'createComment',
            noteId,
          });
        }
        showAlert('Could not post your comment. Please try again.', 'error');
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [noteId, shareToken, invalidate, showAlert],
  );

  const edit = useCallback(
    async (commentId: string, body: string): Promise<Comment | null> => {
      setIsSubmitting(true);
      try {
        const comment = await updateComment({ noteId, commentId, body });
        await invalidate();
        return comment;
      } catch (error) {
        if (isNetworkError(error)) {
          errorReporter.expected('Failed to edit comment (network)', error, {
            feature: 'notes',
            operation: 'updateComment',
            noteId,
          });
        } else {
          errorReporter.critical('Failed to edit comment', error, {
            feature: 'notes',
            operation: 'updateComment',
            noteId,
          });
        }
        showAlert('Could not save your edit. Please try again.', 'error');
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [noteId, invalidate, showAlert],
  );

  const remove = useCallback(
    async (commentId: string): Promise<boolean> => {
      setIsSubmitting(true);
      try {
        await deleteComment({ noteId, commentId });
        await invalidate();
        return true;
      } catch (error) {
        if (isNetworkError(error)) {
          errorReporter.expected('Failed to delete comment (network)', error, {
            feature: 'notes',
            operation: 'deleteComment',
            noteId,
          });
        } else {
          errorReporter.critical('Failed to delete comment', error, {
            feature: 'notes',
            operation: 'deleteComment',
            noteId,
          });
        }
        showAlert('Could not delete the comment. Please try again.', 'error');
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [noteId, invalidate, showAlert],
  );

  return { create, edit, remove, isSubmitting };
};
